import * as Yup from 'yup'
import React, { useCallback, useState } from 'react'
import { Formik, Form } from 'formik'
import RoleSelectorButton from 'components/common/Selectors'
import InputField from 'components/common/Formik/InputField'
import { translate } from 'utils/propKeysTranslations'
import { UserReferenceField } from 'components/common/Formik/UserReferenceField'
import InputOrSelect from 'components/common/Formik/InputOrSelect'
import { Button } from '@mui/material'
import { BooleanField } from 'components/common/Formik/BooleanField'

function RolesForm({
  initialValues,
  onSubmitForm,
  error,
  errorMessage,
  rolesToShow,
  title,
  referenceFields = [],
  inputsWithOptions = [],
  updating,
}) {
  const [roleObject, setRoleObject] = useState(rolesToShow[0])

  const referencesAndInputsWithSelect = referenceFields[
    roleObject.role
  ]?.concat(inputsWithOptions[roleObject.role] || [])

  const inputsToRender = Object.keys(roleObject.validation.fields).map(
    (key) => ({
      label: translate(key),
      name: key,
    }),
  ) // only the TextFields

  const getAllCurrentFields = (roleObject) => {
    // gathers formatted objects for Form's fields
    return {
      ...roleObject.validation.fields,
      ...referenceFields[roleObject.role]
        ?.concat(inputsWithOptions[roleObject.role] || [])
        ?.reduce((p, { fieldName }) => ({ ...p, [fieldName]: '' }), {}),
    }
  }

  const [previousValues, setPreviousValues] = useState(
    collectDesiredValues(getAllCurrentFields(roleObject), initialValues || {}),
  )

  let completeValidationSchema = combineValidations(
    referencesAndInputsWithSelect,
    roleObject.validation,
  )
  if (updating) {
    completeValidationSchema = completeValidationSchema.clone().shape({
      password: completeValidationSchema.fields.password.notRequired(),
      passwordConfirm: completeValidationSchema.fields.password.notRequired(),
    })
  }
  return (
    <div className='py-12 flex-1 w-full'>
      <div
        className='flex bg-gray-100 rounded-lg shadow-2xl overflow-hidden
         mx-auto max-w-[90%] md:max-w-lg lg:max-w-xl'
      >
        <div className='w-full p-8 bg-purple-50'>
          <h1 className='mb-8 text-2xl font-semibold text-gray-600 text-center'>
            {title || 'Registrar usuarios'}
          </h1>
          <Formik
            initialValues={previousValues}
            validationSchema={completeValidationSchema}
            validateOnChange={false}
            onSubmit={(values, { resetForm, setSubmitting }) => {
              onSubmitForm({
                values,
                role: roleObject,
                reset: resetForm,
                setSubmitting,
              })
            }}
            enableReinitialize
          >
            {({
              values,
              isSubmitting,
              getFieldProps,
              initialValues,
              errors,
            }) => (
              <Form className='flex flex-col' autoComplete='off'>
                {/* {console.log(values, errors)} */}
                {rolesToShow?.length > 1 && (
                  <RoleSelectorButton
                    options={rolesToShow}
                    onChange={(roleObject) => {
                      // when the role changes, the previous values object
                      // and the new empty object get combined.
                      setPreviousValues(
                        collectDesiredValues(
                          getAllCurrentFields(roleObject),
                          values,
                        ),
                      )
                      setRoleObject(roleObject)
                    }}
                  />
                )}
                {inputsToRender.map(({ label, name }) => {
                  const type = name.toLowerCase().includes('password')
                    ? 'password'
                    : 'text'

                  return (
                    name === 'allowQR'
                      ? roleObject.role === 'Client' && (
                      <BooleanField
                        fieldName='allowQR'
                        initialValues={initialValues}
                      />
                      )
                      : (
                        <InputField
                          label={label}
                          name={name}
                          type={type}
                          key={name}
                    />
                      )
                  )
                })}
                {referenceFields[roleObject.role]?.map(
                  ({ fieldName, endpoint, data }) => (
                    <UserReferenceField
                      key={fieldName}
                      data={data}
                      fieldName={fieldName}
                      endpoint={endpoint}
                      {...getFieldProps(fieldName)}
                    />
                  ),
                )}
                {inputsWithOptions[roleObject.role]?.map(
                  ({ fieldName, endpoint, data }) => (
                    <InputOrSelect
                      key={fieldName}
                      data={data}
                      fieldName={fieldName}
                      endpoint={endpoint}
                      initialValues={initialValues}
                      {...getFieldProps(fieldName)}
                    />
                  ),
                )}
                <div className='mt-8'>
                  <Button
                    className='text-white font-bold py-2 px-4 w-full rounded-lg'
                    type='submit'
                    disabled={isSubmitting}
                  >
                    {isSubmitting
                      ? 'Cargando...'
                      : updating
                        ? 'Modificar'
                        : 'Crear'}
                  </Button>
                </div>
                {!error ? null : (
                  <span className='text-red-500 text-sm'>
                    {error === 409
                      ? 'Ya existe un usuario con ese email'
                      : `Hay un problema con los datos ingresados: ${errorMessage}`}
                  </span>
                )}
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  )
}

const collectDesiredValues = (desiredValues, currentValues = {}) => {
  const values = { ...desiredValues }
  for (const key in values) {
    if (Object.hasOwnProperty.call(values, key)) {
      values[key] = currentValues[key] || ''
    }
  }
  return values
}

const combineValidations = (validations, initialValidation) => {
  if (!validations?.length) return initialValidation
  return validations.reduce((p, { fieldName, validation }) => {
    return p.concat(Yup.object({ [fieldName]: validation }))
  }, initialValidation)
}

export default RolesForm
